import { useTranslation } from 'react-i18next'
import { DatePicker } from 'antd'
import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'
import styled from 'styled-components'
import type { Dayjs } from 'dayjs'

dayjs.extend(isoWeek)

interface WeekRangePickerProps {
  value: [Date | undefined, Date | undefined]
  onChange: (value: [Date | undefined, Date | undefined]) => void
}

const WeekRangePicker = ({ value, onChange }: WeekRangePickerProps) => {
  const { t } = useTranslation()

  const handleChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    if (!dates || !dates[0]) return

    const startDate = dates[0].startOf('isoWeek')
    const endDate = dates[1]
      ? dates[1].endOf('isoWeek')
      : startDate.endOf('isoWeek')

    const weeksDiff = Math.abs(endDate.diff(startDate, 'week'))
    if (weeksDiff > 3) {
      const maxEndDate = startDate.add(3, 'week').endOf('isoWeek')
      onChange([startDate.toDate(), maxEndDate.toDate()])
      return
    }

    onChange([startDate.toDate(), endDate.toDate()])
  }

  const formatDate = (date: Dayjs) => {
    const startOfWeek = date.startOf('isoWeek')
    const endOfWeek = startOfWeek.endOf('isoWeek')

    return date === initialValue[0]
      ? startOfWeek.format('DD MMM YYYY')
      : endOfWeek.format('DD MMM YYYY')
  }

  const initialValue: [Dayjs | null, Dayjs | null] = value[0]
    ? [
        dayjs(value[0]).startOf('isoWeek'),
        dayjs(value[1] || value[0]).endOf('isoWeek'),
      ]
    : [null, null]

  return (
    <WeekRangePickerContainer>
      <StyledRangePicker
        picker="week"
        value={initialValue}
        onChange={handleChange}
        placeholder={[t('SELECT_WEEK'), t('SELECT_WEEK')]}
        format={formatDate}
        allowClear={false}
        disabledDate={(current) => {
          return current.isAfter(dayjs(), 'day')
        }}
      />
    </WeekRangePickerContainer>
  )
}

export default WeekRangePicker

const WeekRangePickerContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`

const StyledRangePicker = styled(DatePicker.RangePicker)`
  min-width: 400px;
`
